import { useLayoutEffect } from 'react'
import PropTypes from 'prop-types'
import { useChartContext } from '../ChartProvider'

function ComponentScatter ({
  seriesId,
  data,
  name,
  colorBy,
  legendHoverLink,
  xAxisIndex,
  yAxisIndex,
  polarIndex,
  geoIndex,
  calendarIndex,
  symbol,
  symbolSize,
  symbolRotate,
  symbolKeepAspect,
  symbolOffset,
  large,
  largeThreshold,
  cursor,
  label,
  labelLine,
  labelLayout,
  itemStyle,
  emphasis,
  blur,
  select,
  selectedMode,
  progressive,
  progressiveThreshold,
  dimensions,
  encode,
  seriesLayoutBy,
  datasetIndex,
  dataGroupId,
  markPoint,
  markLine,
  markArea,
  clip,
  zlevel,
  z,
  silent,
  animation,
  animationThreshold,
  animationDuration,
  animationEasing,
  animationDelay,
  animationDurationUpdate,
  animationDelayUpdate,
  animationEasingUpdate,
  universalTransition,
  tooltip
}) {
  const { setSeries } = useChartContext()

  useLayoutEffect(() => {
    setSeries(seriesId, {
      type: 'scatter',
      id: seriesId,
      data,
      name,
      colorBy,
      legendHoverLink,
      xAxisIndex,
      yAxisIndex,
      polarIndex,
      geoIndex,
      calendarIndex,
      symbol,
      symbolSize,
      symbolRotate,
      symbolKeepAspect,
      symbolOffset,
      large,
      largeThreshold,
      cursor,
      label,
      labelLine,
      labelLayout,
      itemStyle,
      emphasis,
      blur,
      select,
      selectedMode,
      progressive,
      progressiveThreshold,
      dimensions,
      encode,
      seriesLayoutBy,
      datasetIndex,
      dataGroupId,
      markPoint,
      markLine,
      markArea,
      clip,
      zlevel,
      z,
      silent,
      animation,
      animationThreshold,
      animationDuration,
      animationEasing,
      animationDelay,
      animationDurationUpdate,
      animationDelayUpdate,
      animationEasingUpdate,
      universalTransition,
      tooltip
    })
  }, [
    seriesId,
    data,
    name,
    colorBy,
    legendHoverLink,
    xAxisIndex,
    yAxisIndex,
    polarIndex,
    geoIndex,
    calendarIndex,
    symbol,
    symbolSize,
    symbolRotate,
    symbolKeepAspect,
    symbolOffset,
    large,
    largeThreshold,
    cursor,
    label,
    labelLine,
    labelLayout,
    itemStyle,
    emphasis,
    blur,
    select,
    selectedMode,
    progressive,
    progressiveThreshold,
    dimensions,
    encode,
    seriesLayoutBy,
    datasetIndex,
    dataGroupId,
    markPoint,
    markLine,
    markArea,
    clip,
    zlevel,
    z,
    silent,
    animation,
    animationThreshold,
    animationDuration,
    animationEasing,
    animationDelay,
    animationDurationUpdate,
    animationDelayUpdate,
    animationEasingUpdate,
    universalTransition,
    tooltip,
    setSeries
  ])

  return null
}

ComponentScatter.propTypes = {
  seriesId: PropTypes.string,
  name: PropTypes.string,
  data: PropTypes.array,

  /** The policy to take color from option.color. Valid values:

   'series': assigns the colors in the palette by series, so that all data in the same series are in the same color;
   'data': assigns colors in the palette according to data items, with each data item using a different color. */
  colorBy: PropTypes.oneOf(['series', 'data']),

  /** The coordinate used in the series, whose options are:
   'cartesian2d'
   Use a two-dimensional rectangular coordinate (also known as Cartesian coordinate), with xAxisIndex and yAxisIndex to assign the corresponding axis component.
   'polar'
   Use polar coordinates, with polarIndex to assign the corresponding polar coordinate component. */
  // coordinateSystem: PropTypes.oneOf(['cartesian2d', 'polar']),

  /** Index of x axis to combine with, which is useful for multiple x axes in one chart. */
  xAxisIndex: PropTypes.number,

  /** Index of y axis to combine with, which is useful for multiple y axes in one chart. */
  yAxisIndex: PropTypes.number,

  /** Index of polar coordinate to combine with, which is useful for multiple polar axes in one chart. */
  polarIndex: PropTypes.number,

  /** Index of geographic coordinate to combine with, which is useful for multiple geographic axes in one chart. */
  geoIndex: PropTypes.number,

  /** Index of calendar coordinates to combine with, which is useful for multiple calendar coordinates in one chart. */
  calendarIndex: PropTypes.number,

  /** Whether to enable highlighting chart when legend is being hovered. */
  legendHoverLink: PropTypes.bool,

  /** Symbol of .
   Icon types provided by ECharts includes
   'circle', 'rect', 'roundRect', 'triangle', 'diamond', 'pin', 'arrow', 'none'
   It can be set to an image with 'image://url' , in which URL is the link to an image, or dataURI of an image. */
  symbol: PropTypes.string,

  /** symbol size. It can be set to single numbers like 10, or use an array to represent width and height. For example, [20, 10] means symbol width is 20, and height is10.
   If size of symbols needs to be different, you can set with callback function in the following format */
  symbolSize: PropTypes.oneOfType([PropTypes.number, PropTypes.array]),

  /** Rotate degree of symbol. The negative value represents clockwise. Note that when symbol is set to be 'arrow' in markLine, symbolRotate value will be ignored, and compulsively use tangent angle. */
  symbolRotate: PropTypes.number,

  /** Whether to keep aspect for symbols in the form of path://. */
  symbolKeepAspect: PropTypes.bool,

  /** Offset of symbol relative to original position. By default, symbol will be put in the center position of data. But if symbol is from user-defined vector path or image, you may not expect symbol to be in center. In this case, you may use this attribute to set offset to default position. It can be in absolute pixel value, or in relative percentage value.
   For example, [0, '-50%'] means to move upside side position of symbol height. It can be used to make the arrow in the bottom to be at data position when symbol is pin. */
  symbolOffset: PropTypes.array,

  /** Whether to enable the optimization of large-scale data. It could be set when large data causes performance problem.
   After being enabled, largeThreshold can be used to indicate the minimum number for turning on the optimization.
   But when the optimization enabled, the style of single data item can't be customized any more. */
  large: PropTypes.bool,

  /** The threshold enabling the drawing optimization. */
  largeThreshold: PropTypes.number,

  /** The mouse style when mouse hovers on an element, the same as cursor property in CSS. */
  cursor: PropTypes.string,

  /** Text label of , to explain some data information about graphic item like value, name and so on. label is placed under itemStyle in ECharts 2.x. In ECharts 3, to make the configuration structure flatter, labelis taken to be at the same level with itemStyle, and has emphasis as itemStyle does. */
  label: PropTypes.object,

  /** Configuration of label guide line. */
  labelLine: PropTypes.object,

  /** Unified layout configuration of labels.
   It provide a chance to adjust the labels' (x, y) position, alignment based on the original layout each series provides.
   This option can be a callback with following parameters. */
  labelLayout: PropTypes.object,

  /** The style of the symbol point of broken line. */
  itemStyle: PropTypes.object,

  /** Highlight style of the graphic. */
  emphasis: PropTypes.object,

  /** Configurations of blur state. Available when emphasis.focus is set. */
  blur: PropTypes.object,

  /** Configurations of select state. Available when selectedMode is set. */
  select: PropTypes.object,

  /** Selected mode. It is disabled by default, and you may set it to be true to enable it.
   Besides, it can be set to 'single', 'multiple' or 'series', for single selection, multiple selections and whole series selection. */
  selectedMode: PropTypes.oneOfType([PropTypes.bool, PropTypes.oneOf(['single', 'multiple', 'series'])]),

  /** progressive specifies the amount of graphic elements that can be rendered within a frame (about 16ms) if "progressive rendering" enabled.
   When data amount is from thousand to more than 10 million, it will take too long time to render all of the graphic elements. Since ECharts 4, "progressive rendering" is supported in its workflow, which processes and renders data chunk by chunk alone with each frame, avoiding to block the UI thread of the browser.
   Set progressive: 0 to disable progressive permanently. By default, progressive is auto-enabled when data amount is bigger than progressiveThreshold. */
  progressive: PropTypes.number,

  /** If current data amount is over the threshold, "progressive rendering" is enabled. */
  progressiveThreshold: PropTypes.number,

  /** dimensions can be used to define dimension info for series.data or dataset.source. */
  dimensions: PropTypes.array,

  /** Define what is encoded to for each dimension of data */
  encode: PropTypes.object,

  /** When dataset is used, seriesLayoutBy specifies whether the column or the row of dataset is mapped to the series, namely, the series is "layout" on columns or rows. Optional values:
   'column': by default, the columns of dataset are mapped the series. In this case, each column represents a dimension.
   'row'：the rows of dataset are mapped to the series. In this case, each row represents a dimension. */
  seriesLayoutBy: PropTypes.oneOf(['column', 'row']),

  /** If series.data is not specified, and dataset exists, the series will use dataset. datasetIndex specifies which dataset will be used. */
  datasetIndex: PropTypes.number,

  /** A group ID assigned to all data items in the series.
   This option has a lower priority than groupId, which means when groupId is specified for a certain data item the
   dataGroupId will be simply ignored for that data item. For more information, please see series.data.groupId. */
  dataGroupId: PropTypes.string,

  /** Mark point in a chart. */
  markPoint: PropTypes.object,

  /** Use a line in the chart to illustrate. */
  markLine: PropTypes.object,

  /** Used to mark an area in chart. For example, mark a time interval. */
  markArea: PropTypes.object,

  /** If clip the overflow on the coordinate system. Clip results varies between series */
  clip: PropTypes.bool,

  /** zlevel value of all graphical elements in Line.
   zlevel is used to make layers with Canvas. Graphical elements with different zlevel values will be placed in
   different Canvases, which is a common optimization technique. We can put those frequently changed elements
   (like those with animations) to a separate zlevel. Notice that too many Canvases will increase memory cost,
   and should be used carefully on mobile phones to avoid crash.
   Canvases with bigger zlevel will be placed on Canvases with smaller zlevel. */
  zlevel: PropTypes.number,

  /** z value of all graphical elements in Line, which controls order of drawing graphical components. Components with smaller z values may be overwritten by those with larger z values.
   z has a lower priority to zlevel, and will not create new Canvas. */
  z: PropTypes.number,

  /** Whether to ignore mouse events. Default value is false, for triggering and responding to mouse events. */
  silent: PropTypes.bool,

  /** Whether to enable animation. */
  animation: PropTypes.bool,

  /** Whether to set graphic number threshold to animation. Animation will be disabled when graphic number is larger than threshold. */
  animationThreshold: PropTypes.number,

  /** Duration of the first animation, which supports callback function for different data to have different animation effect: */
  animationDuration: PropTypes.number,

  /** Easing method used for the first animation. Varied easing effects can be found at easing effect example. */
  animationEasing: PropTypes.string,

  /** Delay before updating the first animation, which supports callback function for different data to have different animation effect. */
  animationDelay: PropTypes.number,

  /** Time for animation to complete, which supports callback function for different data to have different animation effect: */
  animationDurationUpdate: PropTypes.number,

  /** Easing method used for animation. */
  animationEasingUpdate: PropTypes.number,

  /** Delay before updating animation, which supports callback function for different data to have different animation effects. */
  animationDelayUpdate: PropTypes.number,

  universalTransition: PropTypes.object,

  tooltip: PropTypes.object
}

ComponentScatter.defaultProps = {
}

export default ComponentScatter
